import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, formatDate, maskMoneyInput, parseInputToApi, moneyToInput } from '@/lib/money';
import type { MoneyString } from '@/lib/money';
import { Modal, Field, TransactionForm } from '@/features/transactions/TransactionForm';
import { CardForm } from './CardForm';
import type { ReferenceData, Transaction } from '@/features/transactions/types';
import { RevolvingDialog } from './RevolvingDialog';

type InvoiceSummary = {
  id: number;
  credit_card_id: number;
  reference: string;
  closing_date: string;
  due_date: string;
  status: string;
  previous_balance: MoneyString;
  purchases_total: MoneyString;
  charges_total: MoneyString;
  credits_total: MoneyString;
  total: MoneyString;
  paid_amount: MoneyString;
  remaining_amount: MoneyString;
  is_fully_paid: boolean;
  is_overdue: boolean;
};

type Card = {
  id: number;
  name: string;
  brand: string | null;
  last4: string | null;
  color: string | null;
  entity_id: number;
  entity_name: string | null;
  closing_day: number;
  due_day: number;
  credit_limit: MoneyString;
  used: MoneyString;
  available_limit: MoneyString;
  status: string;
  paid_invoices: number;
  usage_percent: number;
  revolving_rate_monthly: string | null;
  open_invoices: InvoiceSummary[];
};

export type InvoiceDetail = InvoiceSummary & {
  card: {
    id: number;
    name: string;
    last4: string | null;
    color: string | null;
    entity_id: number;
    entity_name: string | null;
  };
  /**
   * O que ficou em aberto na fatura ANTERIOR — RN-34.
   *
   * É uma sugestão calculada, não um fato gravado: pré-preenche o campo e quem
   * confere contra a fatura do banco corrige. Zero quando a anterior está
   * quitada ou não existe.
   */
  suggested_previous_balance: MoneyString;
  transactions: Array<{
    id: number;
    description: string;
    competence_date: string;
    net_amount: MoneyString;
    category_name?: string | null;
    installment_number: number | null;
    installment_total: number | null;
    installment_plan_id: number | null;
  }>;
  /**
   * Contas quitadas COM este cartão — RF-PAG-06.
   *
   * Não são compras: a despesa já foi reconhecida no lançamento original, e o
   * que entra na fatura é a dívida que trocou de credor. Compõem o total, e por
   * isso precisam de linha — total com um valor que nada explica parece defeito.
   */
  external_payments: Array<{
    id: number;
    description: string;
    amount: MoneyString;
    paid_at: string | null;
    transaction_id: number;
  }>;
  /**
   * Planos de parcelamento desta fatura que deixaram de fechar — RN-31.
   *
   * Chave é o `installment_plan_id`. Vem só os que TÊM divergência, então a
   * ausência da chave já significa "está inteiro".
   */
  plan_divergences: Record<string, { total_original: MoneyString; missing: MoneyString; installments_count: number }>;
  computed_total: MoneyString;
};

export function CardsPage({
  selectedIds,
  initialInvoiceId,
}: {
  selectedIds: number[];
  /**
   * Fatura para já abrir — vem do resumo em Lançamentos.
   *
   * Sem isto, "Detalhar" abriria a tela de Cartões e deixaria a pessoa
   * procurando qual das faturas era. Num cartão com oito faturas em aberto,
   * o desvio é maior que o ganho de ter o botão.
   */
  initialInvoiceId?: number | null;
}) {
  const [openInvoiceId, setOpenInvoiceId] = useState<number | null>(initialInvoiceId ?? null);
  const [payingInvoice, setPayingInvoice] = useState<InvoiceDetail | null>(null);
  const [editing, setEditing] = useState<Card | null>(null);
  const [deleting, setDeleting] = useState<Card | null>(null);
  const [buying, setBuying] = useState<Card | null>(null);
  const [editingPurchase, setEditingPurchase] = useState<Transaction | null>(null);

  /**
   * Editar uma compra da fatura.
   *
   * O lançamento vem do servidor antes de abrir: a fatura devolve só o que a
   * tabela mostra — data, descrição, categoria e valor —, e o formulário
   * precisa do registro inteiro. Abrir com um objeto incompleto salvaria os
   * campos ausentes como vazios, que é o defeito clássico de reaproveitar um
   * resumo como se fosse o dado.
   */
  const abrirEdicao = useMutation({
    mutationFn: (id: number) =>
      api.get<{ data: Transaction }>(`/transactions/${id}`, { entityIds: selectedIds }),
    onSuccess: (r) => setEditingPurchase(r.data),
    onError: (e) =>
      window.alert(e instanceof ApiError ? e.message : 'Não foi possível abrir a compra.'),
  });

  const removePurchase = useMutation({
    mutationFn: (id: number) => api.delete(`/transactions/${id}`, { entityIds: selectedIds }),
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['invoice'] });
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (e) =>
      window.alert(e instanceof ApiError ? e.message : 'Não foi possível excluir a compra.'),
  });

  function confirmarExclusao(id: number, descricao: string, valor: MoneyString) {
    // O texto diz o EFEITO na fatura, e não só "tem certeza?". Excluir uma
    // compra muda o total do que se vai pagar — é isso que precisa ser lido.
    if (
      window.confirm(
        `Excluir "${descricao}" de ${formatMoney(valor)}?\n\n` +
          'O total da fatura é refeito na hora. Se for uma compra parcelada, ' +
          'as outras parcelas continuam nas faturas delas.',
      )
    ) {
      removePurchase.mutate(id);
    }
  }
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const cards = useQuery({
    queryKey: ['cards', selectedIds],
    queryFn: () => api.get<{ data: Card[] }>('/cards', { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const remove = useMutation({
    mutationFn: ({ id, scope }: { id: number; scope: 'nao_pagas' | 'tudo' }) =>
      api.delete<{ message: string }>(`/cards/${id}`, {
        entityIds: selectedIds,
        body: { scope },
      }),
    onSuccess: (res) => {
      setDeleting(null);
      window.alert(res.message);
      refresh();
    },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao excluir o cartão.'),
  });

  function refresh() {
    setEditing(null);
    setCreating(false);
    void queryClient.invalidateQueries({ queryKey: ['cards'] });
    void queryClient.invalidateQueries({ queryKey: ['reference'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  if (cards.isPending) return <Skeleton />;
  if (cards.isError) {
    return (
      <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
        Não foi possível carregar os cartões.
      </p>
    );
  }

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h1 className="text-xl font-semibold tracking-tight">Cartões de crédito</h1>
      <button
        onClick={() => setCreating(true)}
        disabled={!reference.data}
        className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
      >
        + Novo cartão
      </button>
    </div>
  );

  const buyDialog = buying && reference.data && (
    <TransactionForm
      reference={reference.data}
      selectedIds={selectedIds}
      presetCardId={buying.id}
      onClose={() => setBuying(null)}
      onSaved={() => {
        setBuying(null);
        refresh();
        // A fatura mudou de total: a lista de faturas em aberto precisa
        // recarregar junto, senão o cartão mostra o valor de antes.
        void queryClient.invalidateQueries({ queryKey: ['invoice'] });
        void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      }}
    />
  );

  const editPurchaseDialog = editingPurchase && reference.data && (
    <TransactionForm
      transaction={editingPurchase}
      reference={reference.data}
      selectedIds={selectedIds}
      onClose={() => setEditingPurchase(null)}
      onSaved={() => {
        setEditingPurchase(null);
        refresh();
        void queryClient.invalidateQueries({ queryKey: ['invoice'] });
        void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      }}
    />
  );

  const deleteDialog = deleting && (
    <DeleteCardDialog
      card={deleting}
      pending={remove.isPending}
      onClose={() => setDeleting(null)}
      onConfirm={(scope) => remove.mutate({ id: deleting.id, scope })}
    />
  );

  const formDialog = (creating || editing) && reference.data && (
    <CardForm
      card={editing}
      reference={reference.data}
      selectedIds={selectedIds}
      onClose={() => {
        setCreating(false);
        setEditing(null);
      }}
      onSaved={refresh}
    />
  );

  if (cards.data.length === 0) {
    return (
      <div className="mx-auto max-w-6xl">
        {header}
        <div className="mt-8 rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">
            Nenhum cartão cadastrado nas entidades selecionadas.
          </p>
          <button
            onClick={() => setCreating(true)}
            disabled={!reference.data}
            className="mt-3 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            Cadastrar o primeiro cartão
          </button>
        </div>
        {formDialog}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      {header}

      <div className="mt-5 space-y-4">
        {cards.data.map((card) => (
          <div
            key={card.id}
            className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span
                  className="h-9 w-9 shrink-0 rounded-lg"
                  style={{ background: card.color ?? '#64748b' }}
                />
                <div>
                  <p className="font-medium">{card.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {card.last4 && `•••• ${card.last4} · `}
                    {card.entity_name} · fecha dia {card.closing_day} · vence dia {card.due_day}
                  </p>

                  {/*
                    O cartão desativado PRECISA ser reconhecível.
                    Ele aparece na lista só para dar nome ao histórico que
                    sobrou — e sem o selo, pareceria um cartão em uso.
                  */}
                  {card.status === 'cancelado' && (
                    <span className="mt-1 inline-block rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                      desativado · só histórico
                    </span>
                  )}
                </div>
              </div>

              <div className="text-right">
                <p className="text-lg font-semibold">{formatMoney(card.used)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  de {formatMoney(card.credit_limit)} · {formatMoney(card.available_limit)} livre
                </p>
                <div className="mt-1 flex justify-end gap-3 text-[11px]">
                  {/*
                    LANÇAR A COMPRA A PARTIR DO CARTÃO.

                    É onde a pessoa está quando lembra da compra — olhando o
                    cartão e a fatura dele. Obrigá-la a ir até Lançamentos e
                    reencontrar o cartão numa lista é um desvio sem motivo.

                    O formulário é o MESMO de Lançamentos, só que já com o
                    cartão escolhido. Um formulário próprio para cartão teria de
                    repetir categoria, parcelas e contato, e as duas versões
                    divergiriam com o tempo.
                  */}
                  {card.status === 'ativo' && (
                    <button
                      onClick={() => setBuying(card)}
                      disabled={!reference.data}
                      className="font-medium text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-400"
                    >
                      lançar compra
                    </button>
                  )}
                  <button
                    onClick={() => setEditing(card)}
                    disabled={!reference.data}
                    className="text-slate-500 hover:underline disabled:opacity-50 dark:text-slate-400"
                  >
                    editar
                  </button>
                  {/*
                    RNF-25: confirmação antes de excluir — mas não num
                    `window.confirm`. A escolha aqui tem duas saídas com
                    consequências bem diferentes, e uma delas apaga pagamentos
                    já feitos. Isso não cabe num "OK / Cancelar".
                  */}
                  <button
                    onClick={() => setDeleting(card)}
                    className="text-rose-600 hover:underline dark:text-rose-400"
                  >
                    excluir
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className={`h-full rounded-full ${
                  card.usage_percent >= 90
                    ? 'bg-rose-500'
                    : card.usage_percent >= 70
                      ? 'bg-amber-500'
                      : 'bg-indigo-500'
                }`}
                style={{ width: `${Math.min(card.usage_percent, 100)}%` }}
              />
            </div>

            {/* ---------- Faturas ---------- */}
            {card.open_invoices.length > 0 && (
              <div className="mt-4 space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Faturas em aberto
                </p>
                {card.open_invoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-800"
                  >
                    <span className="font-medium">{inv.reference}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      fecha {formatDate(inv.closing_date)} · vence {formatDate(inv.due_date)}
                    </span>
                    {inv.is_overdue && (
                      <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                        vencida
                      </span>
                    )}

                    <span className="ml-auto font-medium">{formatMoney(inv.remaining_amount)}</span>

                    <button
                      onClick={() => setOpenInvoiceId(openInvoiceId === inv.id ? null : inv.id)}
                      className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
                    >
                      {openInvoiceId === inv.id ? 'Fechar' : 'Detalhes'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {openInvoiceId !== null && (
        <InvoiceDetailPanel
          invoiceId={openInvoiceId}
          selectedIds={selectedIds}
          onPay={setPayingInvoice}
          onEdit={(id) => abrirEdicao.mutate(id)}
          onRemove={confirmarExclusao}
        />
      )}

      {payingInvoice && (
        <PayInvoiceDialog
          invoice={payingInvoice}
          selectedIds={selectedIds}
          onClose={() => setPayingInvoice(null)}
          onPaid={() => {
            setPayingInvoice(null);
            void queryClient.invalidateQueries({ queryKey: ['cards'] });
            void queryClient.invalidateQueries({ queryKey: ['invoice'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            void queryClient.invalidateQueries({ queryKey: ['accounts'] });
          }}
        />
      )}

      {formDialog}
      {deleteDialog}
      {buyDialog}
      {editPurchaseDialog}
    </div>
  );
}

// -------------------------------------------------------------------

/**
 * Excluir um cartao — a escolha entre duas saidas bem diferentes.
 *
 * ================================================================
 * POR QUE NAO E UM window.confirm
 * ================================================================
 *
 * Uma das opcoes apaga pagamentos de fatura ja feitos, devolvendo o dinheiro
 * para a conta e mudando saldos. Isso nao cabe num "OK / Cancelar", onde as
 * duas alternativas parecem igualmente inofensivas.
 *
 * O padrao e sempre o menos destrutivo, e a opcao pesada exige um clique
 * deliberado num radio — nunca e o caminho de menor resistencia.
 *
 * E o texto diz NUMEROS, nao advertencias genericas: "3 faturas ja pagas" em
 * vez de "pode haver histórico". Quem decide precisa saber o que perde.
 */
function DeleteCardDialog({
  card,
  pending,
  onClose,
  onConfirm,
}: {
  card: Card;
  pending: boolean;
  onClose: () => void;
  onConfirm: (scope: 'nao_pagas' | 'tudo') => void;
}) {
  const [scope, setScope] = useState<'nao_pagas' | 'tudo'>('nao_pagas');
  const temHistorico = card.paid_invoices > 0;

  return (
    <Modal title={`Excluir ${card.name}`} onClose={onClose}>
      <div className="space-y-4 text-sm">
        <p className="text-slate-600 dark:text-slate-300">
          As faturas futuras e as compras que ainda não foram pagas saem em qualquer um dos casos —
          elas deixam de cobrar em <strong>A pagar</strong> e na previsão.
        </p>

        <div className="space-y-2">
          <label className="flex cursor-pointer gap-3 rounded-lg border border-slate-200 p-3 transition hover:border-indigo-400 dark:border-slate-700">
            <input
              type="radio"
              checked={scope === 'nao_pagas'}
              onChange={() => setScope('nao_pagas')}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Manter o que já foi pago</span>
              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                {temHistorico ? (
                  <>
                    {card.paid_invoices} fatura(s) já paga(s) ficam no histórico, e o cartão
                    continua na lista marcado como desativado — ele é quem dá nome àquele gasto.
                  </>
                ) : (
                  <>
                    Este cartão não tem nenhuma fatura paga, então nada será preservado: ele sai
                    inteiro.
                  </>
                )}
              </span>
            </span>
          </label>

          <label className="flex cursor-pointer gap-3 rounded-lg border border-slate-200 p-3 transition hover:border-rose-400 dark:border-slate-700">
            <input
              type="radio"
              checked={scope === 'tudo'}
              onChange={() => setScope('tudo')}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">Apagar tudo, passado e futuro</span>
              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                Nada deste cartão continua no sistema.
              </span>
            </span>
          </label>
        </div>

        {/*
          O aviso só aparece quando há o que perder, e diz exatamente o quê.
          Aviso permanente vira paisagem e ninguém lê.
        */}
        {scope === 'tudo' && temHistorico && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
            Os pagamentos dessas {card.paid_invoices} fatura(s) também serão apagados, e o dinheiro
            <strong> volta para a conta de onde saiu</strong> — o saldo dela vai mudar. Isso não tem
            como ser desfeito.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm transition hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => onConfirm(scope)}
            className="rounded-lg bg-rose-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
          >
            {pending ? 'Excluindo…' : 'Excluir'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------

function InvoiceDetailPanel({
  invoiceId,
  selectedIds,
  onPay,
  onEdit,
  onRemove,
}: {
  invoiceId: number;
  selectedIds: number[];
  onPay: (invoice: InvoiceDetail) => void;
  /** Abre o formulário de lançamento para corrigir a compra. */
  onEdit?: (transactionId: number) => void;
  /** Exclui a compra — o total da fatura se refaz sozinho. */
  onRemove?: (transactionId: number, description: string, amount: MoneyString) => void;
}) {
  const [revolving, setRevolving] = useState(false);

  const invoice = useQuery({
    queryKey: ['invoice', invoiceId, selectedIds],
    queryFn: () =>
      api.get<{ data: InvoiceDetail }>(`/card-invoices/${invoiceId}`, { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  if (invoice.isPending) {
    return (
      <div className="mt-6 h-48 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
    );
  }

  if (invoice.isError) {
    return <p className="mt-6 text-sm text-rose-600">Erro ao carregar a fatura.</p>;
  }

  const inv = invoice.data;
  // I-06: se o total gravado divergir da soma, a tela avisa em vez de esconder.
  /**
   * Corrigir a data de um pagamento feito com o cartão — RF-LAN-17.
   *
   * `onChange` e não um botão de salvar: o campo é de data, o valor só muda
   * quando o usuário escolhe um dia, e um botão separado criaria o estado
   * "editei e esqueci de salvar" — que neste caso some com a correção.
   */
  async function corrigirData(
    pagamento: { id: number; transaction_id: number },
    novaData: string,
  ) {
    if (!novaData) return;

    try {
      const r = await api.patch<{ meta: { note: string; moved_invoice: boolean } }>(
        `/transactions/${pagamento.transaction_id}/settlements/${pagamento.id}`,
        { paid_at: novaData },
        { entityIds: selectedIds },
      );

      // Só avisa quando a conta mudou de fatura: o caso comum é silencioso, e
      // um alerta a cada ajuste de data treinaria a pessoa a fechá-lo sem ler.
      if (r.meta.moved_invoice) alert(r.meta.note);

      void invoice.refetch();
    } catch (error) {
      alert(error instanceof ApiError ? error.message : 'Não foi possível corrigir a data.');
    }
  }

  const divergent = inv.total !== inv.computed_total;

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-3.5 dark:border-slate-800">
        <h2 className="font-medium">
          Fatura {inv.reference} · {inv.card.name}
        </h2>
        {!inv.is_fully_paid && (
          <div className="ml-auto flex items-center gap-2">
            {/**
              * O botão do rotativo só aparece quando há saldo para rolar OU
              * quando já foi informado — RN-34.
              *
              * Mostrar sempre convidaria a informar encargo em fatura que não
              * rolou nada, e o valor mais provável ali seria zero digitado por
              * engano.
              */}
            {(inv.suggested_previous_balance !== '0.0000' ||
              inv.previous_balance !== '0.0000') && (
              <button
                onClick={() => setRevolving(true)}
                className="rounded-lg border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-800 transition hover:bg-amber-50 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-950/40"
              >
                {inv.previous_balance !== '0.0000' ? 'Editar rotativo' : 'Informar rotativo'}
              </button>
            )}

            <button
              onClick={() => onPay(inv)}
              className="rounded-lg bg-emerald-600 px-3.5 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700"
            >
              Pagar fatura
            </button>
          </div>
        )}
      </div>

      {revolving && (
        <RevolvingDialog
          invoice={inv}
          selectedIds={selectedIds}
          onClose={() => setRevolving(false)}
          onSaved={() => {
            setRevolving(false);
            void invoice.refetch();
          }}
        />
      )}

      {divergent && (
        <p className="border-b border-amber-200 bg-amber-50 px-3.5 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
          Total gravado ({formatMoney(inv.total)}) difere da soma dos lançamentos (
          {formatMoney(inv.computed_total)}). Rode <code>sf invariants --fix</code>.
        </p>
      )}

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 border-b border-slate-200 px-3.5 py-3 text-sm sm:grid-cols-4 dark:border-slate-800">
        <Stat label="Saldo anterior" value={inv.previous_balance} />
        <Stat label="Compras" value={inv.purchases_total} />
        <Stat label="Encargos" value={inv.charges_total} />
        <Stat label="Total" value={inv.total} strong />
        <Stat label="Já pago" value={inv.paid_amount} />
        <Stat label="Em aberto" value={inv.remaining_amount} strong />
      </dl>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">Data</th>
              <th className="px-3 py-2 font-medium">Descrição</th>
              <th className="px-3 py-2 font-medium">Categoria</th>
              <th className="px-3 py-2 text-right font-medium">Valor</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {/**
              * AS CONTAS PAGAS COM O CARTÃO VÊM PRIMEIRO, e marcadas.
              *
              * "Fiz o pagamento pelo cartão, fui em cartão detalhes e não
              * entrou no cartão." Entrou — o total estava certo. Faltava a
              * linha.
              *
              * Marcadas porque não são compras: a despesa já foi contada no
              * lançamento original, e confundi-las com compra faria alguém
              * somar o mês errado ao conferir a fatura.
              */}
            {inv.external_payments.map((e) => (
              <tr key={`ext-${e.id}`} className="bg-indigo-50/40 dark:bg-indigo-950/20">
                {/**
                  * A DATA É EDITÁVEL AQUI — RF-LAN-17.
                  *
                  * "Poderia deixar um campo onde eu consiga editar e eu mesmo
                  * ajusto a data de pagamento?"
                  *
                  * Editar no lugar onde o erro aparece, e não numa tela de
                  * configuração: é aqui que a pessoa percebe que a data está
                  * errada, e mandá-la procurar outro caminho é o que fazia a
                  * correção não acontecer.
                  *
                  * Mudar para outro ciclo MOVE a conta de fatura — a resposta
                  * avisa, porque o valor sumir da fatura aberta parece defeito.
                  */}
                <td className="whitespace-nowrap px-3 py-2">
                  <input
                    type="date"
                    defaultValue={e.paid_at ?? ''}
                    onChange={(ev) => corrigirData(e, ev.target.value)}
                    title="Corrigir a data deste pagamento"
                    className="rounded border border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-indigo-300 focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-900"
                  />
                </td>
                <td className="px-3 py-2">
                  {e.description}
                  <span className="ml-1.5 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                    conta paga no cartão
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-400">—</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatMoney(e.amount)}</td>
                <td className="px-3 py-2" />
              </tr>
            ))}

            {inv.transactions.map((t) => (
              <tr key={t.id}>
                <td className="whitespace-nowrap px-3 py-2">{formatDate(t.competence_date)}</td>
                <td className="px-3 py-2">
                  {t.description}
                  {t.installment_total && t.installment_total > 1 && (
                    <span className="ml-1.5 text-xs text-slate-400">
                      {t.installment_number}/{t.installment_total}
                    </span>
                  )}

                  {/*
                    RN-31 — o parcelamento deixou de fechar.

                    "A diferença passa a constar como divergência do plano,
                    SINALIZADA NA INTERFACE." Sem este aviso, quem excluiu uma
                    parcela nunca fica sabendo que o parcelamento ficou
                    incompleto — e o número só reaparece meses depois, quando a
                    conta não bate com a fatura do banco.

                    Diz o VALOR que falta, não só "atenção": é o que permite
                    decidir se relança a parcela ou aceita.
                  */}
                  {t.installment_plan_id !== null &&
                    inv.plan_divergences?.[String(t.installment_plan_id)] && (
                      <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                        faltam{' '}
                        {formatMoney(
                          inv.plan_divergences[String(t.installment_plan_id)]?.missing ?? '0',
                        )}{' '}
                        do parcelamento
                      </span>
                    )}
                </td>
                <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                  {t.category_name ?? '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-medium">
                  {formatMoney(t.net_amount)}
                </td>

                {/*
                  ================================================================
                  EDITAR E EXCLUIR A COMPRA — é aqui que ela mora
                  ================================================================

                  Em Lançamentos as compras aparecem resumidas, uma linha por
                  fatura. O detalhe é este, e é onde as ações precisam estar —
                  senão o resumo manda para uma tela que também não deixa agir.

                  Não é o mesmo que pagar a fatura: pagar liquida a obrigação
                  inteira; isto corrige ou apaga UMA compra. Excluir refaz o
                  total da fatura sozinho (o observer cuida disso).
                */}
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <button
                    onClick={() => onEdit?.(t.id)}
                    className="rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-indigo-600"
                    title="Editar esta compra"
                  >
                    editar
                  </button>
                  <button
                    onClick={() => onRemove?.(t.id, t.description, t.net_amount)}
                    className="ml-1 rounded-lg px-2 py-1 text-xs text-slate-400 transition hover:text-rose-600"
                    title="Excluir esta compra"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="border-t border-slate-200 px-3.5 py-2 text-[11px] text-slate-400 dark:border-slate-800">
        Cada compra já foi reconhecida como despesa na data em que aconteceu. Pagar a fatura apenas
        liquida a obrigação — não gera despesa nova.
      </p>
    </div>
  );
}

function Stat({ label, value, strong }: { label: string; value: MoneyString; strong?: boolean }) {
  return (
    <div className="flex justify-between sm:block">
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className={strong ? 'font-semibold' : ''}>{formatMoney(value)}</dd>
    </div>
  );
}

// -------------------------------------------------------------------

export function PayInvoiceDialog({
  invoice,
  selectedIds,
  onClose,
  onPaid,
}: {
  invoice: InvoiceDetail;
  selectedIds: number[];
  onClose: () => void;
  onPaid: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);

  /**
   * TODAS as contas do usuário, não só as da entidade do cartão — RN-13.
   *
   * "Em A pagar ficou tudo ok, porém somente em pagamento de cartão não está
   * abrindo as contas de outras entidades." Era verdade: o `filter` que estava
   * aqui deixava passar só a entidade do cartão, enquanto a baixa de conta
   * comum já mostrava as duas.
   *
   * Pagar a fatura da Empresa do bolso da PF é tão comum quanto pagar uma conta
   * dela, e a lista curta obrigava ao mesmo de sempre: registrar numa conta que
   * não pagou, ou não registrar. As contas do cartão continuam vindo primeiro —
   * o caso comum não pode ficar mais difícil por causa do raro.
   */
  const accounts = useQuery({
    queryKey: ['reference-accounts', selectedIds],
    queryFn: () =>
      api.get<{
        data: {
          accounts: Array<{ id: number; name: string; entity_id: number; entity_name: string }>;
        };
      }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data.accounts,
  });

  const todas = accounts.data ?? [];
  const daEntidade = todas.filter((a) => a.entity_id === invoice.card.entity_id);
  const deOutras = todas.filter((a) => a.entity_id !== invoice.card.entity_id);

  const [accountId, setAccountId] = useState<string>('');

  /** A conta escolhida, quando for de outra entidade — dispara o aviso. */
  const contaDeOutraEntidade = deOutras.find((a) => String(a.id) === accountId) ?? null;

  const [paymentDate, setPaymentDate] = useState(today);
  const [amountMasked, setAmountMasked] = useState(
    moneyToInput(invoice.remaining_amount),
  );

  const amount = parseInputToApi(amountMasked);
  const isPartial = Number(amount) < Number(invoice.remaining_amount);

  const pay = useMutation({
    mutationFn: () =>
      api.post(`/card-invoices/${invoice.id}/pay`, {
        account_id: Number(accountId),
        payment_date: paymentDate,
        amount,
      }, { entityIds: selectedIds }),
    onSuccess: onPaid,
  });

  const error = pay.error instanceof ApiError ? pay.error : null;

  return (
    <Modal title={`Pagar fatura ${invoice.reference}`} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); pay.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
          </div>
        )}

        {/* O usuário precisa entender o que está acontecendo, não só clicar.
            Esta é a regra que a maioria dos sistemas erra (RN-22 / CA-02). */}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
          <p className="font-medium text-indigo-900 dark:text-indigo-200">
            Isto é uma transferência, não uma despesa.
          </p>
          <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
            As compras já viraram despesa na data em que foram feitas. Pagar a fatura move dinheiro
            da conta para o cartão e libera o limite — sem contar o gasto duas vezes.
          </p>
        </div>

        <dl className="rounded-lg bg-slate-50 px-3.5 py-3 text-sm dark:bg-slate-800/50">
          <div className="flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">Total da fatura</dt>
            <dd>{formatMoney(invoice.total)}</dd>
          </div>
          {invoice.paid_amount !== '0.0000' && (
            <div className="mt-0.5 flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Já pago</dt>
              <dd>{formatMoney(invoice.paid_amount)}</dd>
            </div>
          )}
          <div className="mt-0.5 flex justify-between font-medium">
            <dt>Em aberto</dt>
            <dd>{formatMoney(invoice.remaining_amount)}</dd>
          </div>
        </dl>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor" required>
            <input
              value={amountMasked}
              onChange={(e) => setAmountMasked(maskMoneyInput(e.target.value))}
              required
              inputMode="numeric"
              autoFocus
              className="input w-full text-right"
            />
          </Field>
          <Field label="Data" required>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              required
              className="input w-full"
            />
          </Field>
        </div>

        <Field label="Conta de origem" required>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            required
            disabled={todas.length === 0}
            className="input w-full"
          >
            {/* Lista vazia enquanto carrega não é "não há conta": dizer isso
                antes da resposta chegar acusa o sistema de perder cadastro. */}
            <option value="">
              {accounts.isPending
                ? 'Carregando…'
                : todas.length === 0
                  ? 'Nenhuma conta disponível'
                  : 'Selecione'}
            </option>
            {daEntidade.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
            {deOutras.length > 0 && (
              <optgroup label="Contas de outra entidade">
                {deOutras.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.entity_name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </Field>

        {/**
          * CONTA DE OUTRA ENTIDADE: registra, e diz o que está registrando.
          *
          * O mesmo aviso da baixa de conta comum, pelo mesmo motivo — sem ele o
          * caixa de uma entidade cairia e a dívida da outra também, sem nada
          * ligando as duas pontas. Com ele, quem paga sabe que fica um saldo
          * entre as entidades, e sabe como zerá-lo.
          */}
        {contaDeOutraEntidade && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
            <p className="font-medium text-indigo-900 dark:text-indigo-200">
              O dinheiro sai da conta de {contaDeOutraEntidade.entity_name}.
            </p>
            <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
              A fatura continua sendo de{' '}
              <strong>{invoice.card.entity_name ?? 'outra entidade'}</strong>, e fica registrado que{' '}
              {invoice.card.entity_name ?? 'ela'} deve esse valor a {contaDeOutraEntidade.entity_name}.
              O saldo aparece no painel e some quando você fizer a transferência de{' '}
              <strong>reembolso</strong> entre as duas.
            </p>
          </div>
        )}

        {isPartial && Number(amount) > 0 && (
          <p className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            Pagamento parcial. O saldo de{' '}
            <strong>
              {formatMoney((Number(invoice.remaining_amount) - Number(amount)).toFixed(2))}
            </strong>{' '}
            rola para a próxima fatura e passa a render juros de rotativo — que aí sim são despesa.
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={pay.isPending || Number(amount) <= 0 || !accountId}
            className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {pay.isPending ? 'Pagando…' : 'Confirmar pagamento'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Skeleton() {
  return (
    <div className="mx-auto max-w-6xl animate-pulse">
      <div className="h-6 w-40 rounded bg-slate-200 dark:bg-slate-800" />
      <div className="mt-5 space-y-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-40 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
    </div>
  );
}
